import { useState, useEffect } from 'react';
import { cookieUtils } from 'helpers/cookiesUtils';
import useOnFetch from './useOnFetch';

const GUEST_ID_KEY = 'guest_id';


let _pendingFetch = null;

const getStoredId = () => {
    try {
        const stored = localStorage.getItem(GUEST_ID_KEY);
        return stored ? parseInt(stored, 10) : null;
    } catch {
        return null;
    }
};

const storeId = (id) => {
    try {
        localStorage.setItem(GUEST_ID_KEY, id.toString());
    } catch {
        console.error('[useRegisterGuest] Failed to store guest ID');
    }
};

/**
 * Standalone utility — safe to call outside hook context (e.g. at login/logout time)
 * without triggering a new guest registration.
 */
export const clearStoredGuestId = () => {
    try {
        localStorage.removeItem(GUEST_ID_KEY);
        _pendingFetch = null;
    } catch {
        console.error('[useRegisterGuest] Failed to clear guest ID');
    }
};

function useRegisterGuest() {
    const fetch = useOnFetch();

    const [guestId, setGuestId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const generateGuestId = async () => {
        if (!_pendingFetch) {
            _pendingFetch = fetch({ url: '/guest/login', method: 'post' })
                .then((data) => {
                    if (data?.code === 0 && data.data?.guest_id) {
                        const id = data.data.guest_id;
                        storeId(id);
                        return id;
                    }
                    throw new Error('Invalid response from guest login API');
                })
                .finally(() => {
                    _pendingFetch = null;
                });
        }

        setLoading(true);
        setError(null);

        try {
            const id = await _pendingFetch;
            setGuestId(id);
            return id;
        } catch (err) {
            setError(err.message || 'Failed to generate guest ID');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            // Skip guest registration for authenticated users (therapist, admin)
            const cookies = cookieUtils.getCookies();
            if (cookies?.user?.id) return;

            const storedId = getStoredId();
            if (storedId) {
                setGuestId(storedId);
                return;
            }

            try {
                await generateGuestId();
            } catch (err) {
                console.error('[useRegisterGuest] Failed to initialize guest ID', err);
            }
        };

        init();
    }, []);

    const refreshGuestId = () => generateGuestId();

    const clearGuestId = () => {
        clearStoredGuestId();
        setGuestId(null);
    };

    return {
        guestId,
        loading,
        error,
        refreshGuestId,
        clearGuestId,
        isGuestRegistered: !!guestId,
    };
}

export default useRegisterGuest;
